"use client";

/**
 * ConfirmDialog — confirmação de ação, substituto do `window.confirm`.
 *
 * ## Por que existe
 *
 * `window.confirm` tinha dois defeitos que se somavam:
 *
 * 1. **Visual.** O alerta nativo do Chrome não tem tema, não tem tipografia
 *    do produto e exibe o hostname acima do texto. Num produto vendido a
 *    prefeituras, ele parece um erro do navegador, não uma decisão do
 *    sistema.
 *
 * 2. **Testabilidade.** `window.confirm` bloqueia a thread e vive fora do
 *    DOM: Playwright não o enxerga e, por padrão, **auto-dispensa** o
 *    diálogo — o que faz a confirmação retornar `false` e a ação nunca
 *    acontecer. Na prática, toda ação atrás de um `confirm` nativo era
 *    intestável, incluindo a exclusão de processo licitatório.
 *
 * ## Contrato
 *
 * `onConfirm` pode ser assíncrono. Enquanto a promise não resolve, o diálogo
 * fica aberto com os botões desabilitados — fechar no meio esconderia o
 * resultado de uma ação já em curso. Se ela **rejeitar**, a mensagem aparece
 * dentro do próprio diálogo (era o papel do `window.alert`) e a pessoa pode
 * tentar de novo. Só um `onConfirm` bem-sucedido fecha o diálogo.
 *
 * Quem chama não precisa tratar erro: basta deixar a promise rejeitar.
 *
 * ## Testes
 *
 * `testId` gera três âncoras estáveis:
 *   - `${testId}-dialog`   → o conteúdo do diálogo
 *   - `${testId}-confirmar`→ botão de confirmação
 *   - `${testId}-cancelar` → botão de cancelamento
 */
import * as React from "react";

import { cn } from "../../lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./alert-dialog";
import { buttonVariants } from "./button";

export interface ConfirmDialogProps {
  /** Elemento que abre o diálogo (recebe `asChild` — passe um `<Button>`). */
  trigger: React.ReactNode;
  /** Título curto e afirmativo ("Excluir processo"). */
  title: string;
  /** Texto que explica a consequência e se ela é reversível. */
  description: React.ReactNode;
  /** Rótulo do botão que executa a ação. Default: "Confirmar". */
  confirmLabel?: string;
  /** Rótulo do botão que aborta. Default: "Cancelar". */
  cancelLabel?: string;
  /**
   * Ação destrutiva pinta o botão de confirmação em vermelho. Default `true`
   * — quem abre um confirm normalmente está prestes a destruir algo; o caso
   * neutro é que precisa se declarar.
   */
  destructive?: boolean;
  /**
   * Executa a ação. Pode ser assíncrona. Rejeitar exibe a mensagem dentro do
   * diálogo e o mantém aberto; resolver fecha.
   */
  onConfirm: () => void | Promise<void>;
  /** Prefixo dos `data-testid`. Default: "confirm". */
  testId?: string;
}

export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  destructive = true,
  onConfirm,
  testId = "confirm",
}: ConfirmDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  function handleOpenChange(next: boolean) {
    // Fechar no meio de uma requisição em voo deixaria a pessoa sem saber se
    // a ação foi concluída. Enquanto pendente, o diálogo não fecha.
    if (pending) return;
    setOpen(next);
    if (!next) setError(null);
  }

  async function handleConfirm(event: React.MouseEvent<HTMLButtonElement>) {
    // Radix fecha o AlertDialog ao clicar em Action. Aqui o fechamento é
    // decidido pelo resultado de `onConfirm`, não pelo clique.
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await onConfirm();
      setPending(false);
      setOpen(false);
    } catch (e) {
      setPending(false);
      setError(e instanceof Error ? e.message : "Erro inesperado");
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent
        className="min-w-0 grid-cols-[minmax(0,1fr)] max-h-[85dvh] overflow-y-auto [overflow-wrap:anywhere]"
        data-testid={`${testId}-dialog`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {error ? (
          <p
            role="alert"
            data-testid={`${testId}-erro`}
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel
            className="min-h-11"
            disabled={pending}
            data-testid={`${testId}-cancelar`}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => void handleConfirm(e)}
            disabled={pending}
            data-testid={`${testId}-confirmar`}
            className={cn(
              "min-h-11",
              destructive &&
                buttonVariants({ variant: "destructive", size: "sm" }),
            )}
          >
            {pending ? "Aguarde…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
